import { computed, ref, watch, type Ref } from 'vue'

/**
 * The composer's "improve my prompt" wand (`ChatInput.vue`), MAR-408.
 *
 * It used to replace the user's draft with the rewrite and keep no copy, and on failure it only
 * logged, so the spinner stopped exactly as it does after a no-op. Now:
 *
 * - **Snapshot and revert.** The draft as typed (untrimmed) is kept when a rewrite lands, and
 *   `canRevert` stays true for as long as the composer still holds that exact rewrite. The first
 *   edit — or a send, which clears the draft — drops the snapshot, so the undo never resurrects an
 *   old draft over newer work.
 * - **Failure is state, not a log line.** `failed` is set when the request throws or comes back
 *   empty (an empty rewrite would wipe the draft), and the draft is left as it was. The component
 *   renders it inline; toasts are not used here. It clears on the next edit or the next attempt.
 *
 * The request itself is injected so this is testable without Nuxt; the component passes
 * `apiFetch('/api/prompt/improve')`.
 */
export function usePromptImprove(opts: {
  draft: Ref<string>
  /** Whether the wand is available at all (not disabled by the host, chat not busy). */
  enabled: () => boolean
  /** Returns the rewrite of the (trimmed) prompt. */
  request: (prompt: string) => Promise<string>
}) {
  const { draft } = opts
  const improving = ref(false)
  const failed = ref(false)
  const snapshot = ref<{ before: string; after: string } | null>(null)

  const canRevert = computed(() => !improving.value && snapshot.value !== null && draft.value === snapshot.value.after)
  const wandReady = computed(() => opts.enabled() && draft.value.trim().length > 10 && !improving.value)

  // Any edit that moves the draft off the rewrite ends the undo window and dismisses a stale error.
  watch(draft, (value) => {
    if (improving.value) return
    if (snapshot.value && value !== snapshot.value.after) snapshot.value = null
    failed.value = false
  })

  async function improve() {
    if (!wandReady.value) return
    const before = draft.value
    const text = before.trim()
    if (!text) return
    improving.value = true
    failed.value = false
    try {
      const improved = (await opts.request(text)).trim()
      if (!improved) throw new Error('empty rewrite')
      snapshot.value = { before, after: improved }
      draft.value = improved
    } catch (err) {
      console.error('Failed to improve prompt:', err)
      failed.value = true
    } finally {
      improving.value = false
    }
  }

  function revert() {
    if (!canRevert.value) return
    const { before } = snapshot.value!
    snapshot.value = null
    draft.value = before
  }

  return { improving, failed, canRevert, wandReady, improve, revert }
}
